import { useRef, useEffect } from 'react'

/**
 * Blocks concurrent execution of an async function.
 * If the function is already running, it will not start a new execution.
 * Accounts for component unmounting to prevent memory leaks or state updates on unmounted components.
 * Also catches errors.
 *
 * TODO: Could use a parameter to change strategy to cancel the previous execution instead of blocking the new one, or queue the calls, if those become useful.
 */
export function useLockedAsync<T extends (...args: unknown[]) => Promise<unknown>>(asyncFunc: T) {
  const isRunningRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const run = async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>> | undefined> => {
    if (isRunningRef.current) {
      return
    }

    isRunningRef.current = true
    try {
      return (await asyncFunc(...args)) as Awaited<ReturnType<T>>
    } catch (err) {
      if (!isMountedRef.current) {
        // eslint-disable-next-line no-console
        console.warn('Async function failed after component unmounted:', err)
        return
      }
      throw err
    } finally {
      isRunningRef.current = false
    }
  }

  return run
}
